import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Paper, Button, IconButton, Tabs, Tab, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Chip, Tooltip, Typography,
  CircularProgress
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import StopIcon from "@material-ui/icons/Stop";
import { toast } from "react-toastify";
import { useParams, useHistory } from "react-router-dom";
import MainContainer from "../../components/MainContainer";
import { displayClientName } from "../../utils/clientName";
import PageHeader from "../../components/PageHeader";
import api from "../../services/api";
import useConfirm from "../../hooks/useConfirm";

const useStyles = makeStyles(theme => ({
  body: { flex: 1, minHeight: 0, overflow: "auto", paddingTop: theme.spacing(2) },
  tabs: { flexBasis: "100%", minHeight: 40, marginBottom: theme.spacing(-0.5) },
  kanban: { display: "flex", gap: 8, overflowX: "auto", paddingBottom: 12, minHeight: 480 },
  column: {
    flex: "0 0 280px", background: theme.palette.type === "dark" ? "#222" : "#f5f5f5",
    borderRadius: 6, padding: 8, display: "flex", flexDirection: "column"
  },
  colTitle: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 4px 8px" },
  card: {
    background: theme.palette.background.paper, padding: 8, borderRadius: 4, marginBottom: 6,
    cursor: "grab", boxShadow: "0 1px 2px rgba(0,0,0,.08)"
  },
  pri: { fontSize: 10, padding: "2px 6px", borderRadius: 8, marginRight: 4 },
  list: {},
  taskRow: { display: "grid", gridTemplateColumns: "60px 1fr 120px 100px 120px 120px 120px 80px", padding: "6px 4px", borderBottom: "1px solid rgba(0,0,0,.07)", alignItems: "center" }
}));

const PRIORITIES = [
  { value: "low", label: "Baixa", color: "#9e9e9e" },
  { value: "medium", label: "Média", color: "#2196F3" },
  { value: "high", label: "Alta", color: "#FF9800" },
  { value: "urgent", label: "Urgente", color: "#F44336" }
];
const STATUSES = ["open", "in_progress", "review", "blocked", "done", "cancelled"];
const STATUS_LABEL = {
  active: "Ativo", on_hold: "Pausado", done: "Concluído", archived: "Arquivado"
};

// O cabecalho era uma fileira de Chips "Status: x", "Owner: y", "Prazo: z".
// O rotulo repetido em cada chip custava mais espaco que a informacao.
const projectSubtitle = (p, st) => {
  const partes = [STATUS_LABEL[p.status] || p.status];
  if (p.client) partes.push(displayClientName(p.client));
  if (p.owner?.name) partes.push(`Resp.: ${p.owner.name}`);
  if (p.dueDate) partes.push(`Prazo: ${p.dueDate}`);
  if (st) {
    partes.push(`${st.done}/${st.total} tarefas`);
    partes.push(`${st.spent}h de ${st.estimate}h estimadas`);
  }
  return partes.join(" · ");
};
const TYPES = ["task", "bug", "feature", "improvement"];

const ProjectDetail = () => {
  const classes = useStyles();
  const [confirm, confirmDialog] = useConfirm();
  const { id } = useParams();
  const history = useHistory();
  const [project, setProject] = useState(null);
  const [board, setBoard] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [tab, setTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [clientLoading, setClientLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const clientSearchTimer = useRef(null);
  const [sprints, setSprints] = useState([]);
  const [stats, setStats] = useState(null);
  const [taskDlg, setTaskDlg] = useState({ open: false, editing: null, form: {} });
  const [colDlg, setColDlg] = useState({ open: false, editing: null, boardId: null, form: {} });
  const [sprintDlg, setSprintDlg] = useState({ open: false, editing: null, form: {} });
  const [filterSprint, setFilterSprint] = useState("");
  const [filterAssignee, setFilterAssignee] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
      setSprints(data.sprints || []);
      const def = (data.boards || []).find(b => b.isDefault) || (data.boards || [])[0];
      setBoard(def || null);
      const params = { projectId: id };
      if (def) params.boardId = def.id;
      if (filterSprint) params.sprintId = filterSprint;
      if (filterAssignee) params.assigneeId = filterAssignee;
      const tRes = await api.get("/tasks", { params });
      setTasks(tRes.data || []);
      const sRes = await api.get(`/projects/${id}/stats`);
      setStats(sRes.data);
    } catch (e) { toast.error("Erro ao carregar projeto"); }
  }, [id, filterSprint, filterAssignee]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/users").then(r => setUsers(r.data?.users || [])).catch(() => {});
  }, []);

  // busca assíncrona de clientes (3+ letras)
  useEffect(() => {
    if (clientSearchTimer.current) clearTimeout(clientSearchTimer.current);
    const term = (clientSearch || "").trim();
    if (term.length < 3) { setClientLoading(false); return undefined; }
    setClientLoading(true);
    clientSearchTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/clients", { params: { searchParam: term } });
        const arr = data?.clients ?? data?.data ?? data;
        const list = Array.isArray(arr) ? arr : [];
        setClientOptions(prev => {
          if (selectedClient && !list.find(c => c.id === selectedClient.id)) return [selectedClient, ...list];
          return list;
        });
      } catch { setClientOptions(selectedClient ? [selectedClient] : []); }
      finally { setClientLoading(false); }
    }, 300);
    return () => clientSearchTimer.current && clearTimeout(clientSearchTimer.current);
  }, [clientSearch, selectedClient]);

  if (!project) {
    return (
      <MainContainer>
        <PageHeader title="Carregando…" onBack={() => history.push("/projects")} backLabel="Voltar para Projetos" />
      </MainContainer>
    );
  }

  const columns = (board?.columns || []).slice().sort((a, b) => a.position - b.position);
  const tasksByColumn = (colId) => tasks.filter(t => t.columnId === colId).sort((a, b) => a.position - b.position);

  /* ===== Drag & drop nativo ===== */
  const onDragStart = (e, taskId) => { e.dataTransfer.setData("text/task-id", String(taskId)); };
  const onDropColumn = async (e, colId) => {
    e.preventDefault();
    const taskId = Number(e.dataTransfer.getData("text/task-id"));
    if (!taskId) return;
    const colTasks = tasksByColumn(colId).filter(t => t.id !== taskId).map(t => t.id);
    colTasks.push(taskId);
    try {
      await api.post(`/tasks/${taskId}/move`, { columnId: colId, position: colTasks.length - 1, targetOrder: colTasks });
      load();
    } catch { toast.error("Falha ao mover"); }
  };

  /* ===== Tasks ===== */
  const openNewTask = (colId) => {
    setSelectedClient(null);
    setClientOptions([]);
    setClientSearch("");
    setTaskDlg({
      open: true, editing: null, form: {
        title: "", priority: "medium", status: "open", type: "task",
        projectId: project.id, boardId: board?.id || null, columnId: colId || (columns[0]?.id || null),
        sprintId: filterSprint || null
      }
    });
  };
  const openEditTask = (t) => {
    const c = t.client || null;
    setSelectedClient(c);
    setClientOptions(c ? [c] : []);
    setClientSearch("");
    setTaskDlg({ open: true, editing: t, form: { ...t } });
  };
  const saveTask = async () => {
    try {
      if (!taskDlg.form.title) return toast.warn("Informe o título");
      if (taskDlg.editing) await api.put(`/tasks/${taskDlg.editing.id}`, taskDlg.form);
      else await api.post("/tasks", taskDlg.form);
      setTaskDlg({ open: false, editing: null, form: {} });
      load();
    } catch { toast.error("Erro ao salvar tarefa"); }
  };
  const deleteTask = async (t) => {
    if (!(await confirm({ title: `Excluir a tarefa "${t.title}"?`, message: "Não dá para desfazer." }))) return;
    await api.delete(`/tasks/${t.id}`); load();
  };
  const startTimer = async (t) => { await api.post(`/tasks/${t.id}/timer/start`); toast.success("Timer iniciado"); };
  const stopTimer = async (t) => { await api.post(`/tasks/${t.id}/timer/stop`); toast.success("Timer encerrado"); load(); };

  /* ===== Columns ===== */
  const openNewCol = () => setColDlg({ open: true, editing: null, boardId: board.id, form: { name: "", color: "#2196F3" } });
  const openEditCol = (c) => setColDlg({ open: true, editing: c, boardId: board.id, form: { ...c } });
  const saveCol = async () => {
    try {
      if (colDlg.editing) await api.put(`/board-columns/${colDlg.editing.id}`, colDlg.form);
      else await api.post("/board-columns", { ...colDlg.form, boardId: colDlg.boardId });
      setColDlg({ open: false, editing: null, boardId: null, form: {} });
      load();
    } catch { toast.error("Erro ao salvar coluna"); }
  };
  const deleteCol = async (c) => {
    if (!(await confirm({ title: `Excluir a coluna "${c.name}"?`, message: "As tarefas não são apagadas — ficam sem coluna e voltam para o backlog." }))) return;
    await api.delete(`/board-columns/${c.id}`); load();
  };

  /* ===== Sprints ===== */
  const openNewSprint = () => setSprintDlg({ open: true, editing: null, form: { name: "", projectId: project.id } });
  const openEditSprint = (s) => setSprintDlg({ open: true, editing: s, form: { ...s } });
  const saveSprint = async () => {
    try {
      if (!sprintDlg.form.name) return toast.warn("Informe o nome");
      if (sprintDlg.editing) await api.put(`/sprints/${sprintDlg.editing.id}`, sprintDlg.form);
      else await api.post("/sprints", sprintDlg.form);
      setSprintDlg({ open: false, editing: null, form: {} });
      load();
    } catch { toast.error("Erro ao salvar sprint"); }
  };
  const startSprint = async (s) => { await api.post(`/sprints/${s.id}/start`); load(); };
  const closeSprint = async (s) => {
    if (!(await confirm({ title: `Encerrar o sprint "${s.name}"?`, message: "As tarefas ainda em aberto retornam para o backlog." }))) return;
    await api.post(`/sprints/${s.id}/close`, { moveOpenTasksTo: null }); load();
  };
  const deleteSprint = async (s) => {
    if (!(await confirm({ title: `Excluir o sprint "${s.name}"?`, message: "As tarefas do sprint não são apagadas." }))) return;
    await api.delete(`/sprints/${s.id}`); load();
  };

  return (
    <MainContainer>
      {confirmDialog}
      <PageHeader
        title={project.name}
        count={project.code || undefined}
        subtitle={projectSubtitle(project, stats)}
        onBack={() => history.push("/projects")}
        backLabel="Voltar para Projetos"
        actions={
          <>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={openNewCol}>Coluna</Button>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={openNewSprint}>Sprint</Button>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => openNewTask(null)}>
              Nova tarefa
            </Button>
          </>
        }
        filters={
          <>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} indicatorColor="primary" textColor="primary"
              className={classes.tabs}>
              <Tab label="Kanban" />
              <Tab label="Lista" />
              <Tab label="Sprints" />
            </Tabs>
            <TextField select label="Sprint" style={{ minWidth: 180 }}
              value={filterSprint} onChange={e => setFilterSprint(e.target.value)}>
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="null">Sem sprint</MenuItem>
              {sprints.map(s => <MenuItem key={s.id} value={s.id}>{s.name} ({s.status})</MenuItem>)}
            </TextField>
            <TextField select label="Responsável" style={{ minWidth: 180 }}
              value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              {users.map(u => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
            </TextField>
          </>
        }
      />

      <div className={classes.body}>
        {tab === 0 && (
          <div className={classes.kanban} style={{ marginTop: 12 }}>
            {columns.map(col => (
              <div key={col.id} className={classes.column}
                onDragOver={e => e.preventDefault()}
                onDrop={e => onDropColumn(e, col.id)}>
                <div className={classes.colTitle}>
                  <strong style={{ color: col.color || undefined }}>{col.name}</strong>
                  <span>
                    <Tooltip title="Adicionar"><IconButton size="small" onClick={() => openNewTask(col.id)}><AddIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Editar"><IconButton size="small" onClick={() => openEditCol(col)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Excluir"><IconButton size="small" onClick={() => deleteCol(col)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </span>
                </div>
                {tasksByColumn(col.id).map(t => {
                  const pri = PRIORITIES.find(p => p.value === t.priority) || PRIORITIES[1];
                  return (
                    <div key={t.id} className={classes.card} draggable
                      onDragStart={e => onDragStart(e, t.id)}
                      onClick={() => openEditTask(t)}>
                      <div style={{ marginBottom: 4 }}>
                        <span className={classes.pri} style={{ background: pri.color, color: "#fff" }}>{pri.label}</span>
                        <span style={{ fontSize: 10, opacity: 0.6 }}>#{t.id}</span>
                      </div>
                      <div style={{ fontSize: 13 }}>{t.title}</div>
                      {t.assignee?.name && <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>👤 {t.assignee.name}</div>}
                      {t.dueDate && <div style={{ fontSize: 11, opacity: 0.7 }}>📅 {t.dueDate}</div>}
                    </div>
                  );
                })}
                {tasksByColumn(col.id).length === 0 && (
                  <div style={{ fontSize: 11, opacity: 0.5, textAlign: "center", padding: 12 }}>vazio</div>
                )}
              </div>
            ))}
            {columns.length === 0 && <div>Sem colunas. Crie uma para começar.</div>}
          </div>
        )}

        {tab === 1 && (
          <div className={classes.list} style={{ marginTop: 12 }}>
            <div className={classes.taskRow} style={{ fontWeight: 600 }}>
              <div>#</div><div>Título</div><div>Status</div><div>Prio.</div>
              <div>Responsável</div><div>Sprint</div><div>Prazo</div><div align="right">Ações</div>
            </div>
            {tasks.map(t => {
              const sprint = sprints.find(s => s.id === t.sprintId);
              return (
                <div key={t.id} className={classes.taskRow}>
                  <div>{t.id}</div>
                  <div style={{ cursor: "pointer" }} onClick={() => openEditTask(t)}>{t.title}</div>
                  <div>{t.status}</div>
                  <div>{t.priority}</div>
                  <div>{t.assignee?.name || "-"}</div>
                  <div>{sprint?.name || "-"}</div>
                  <div>{t.dueDate || "-"}</div>
                  <div>
                    <IconButton size="small" onClick={() => startTimer(t)}><PlayArrowIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => stopTimer(t)}><StopIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => deleteTask(t)}><DeleteIcon fontSize="small" /></IconButton>
                  </div>
                </div>
              );
            })}
            {tasks.length === 0 && <div style={{ padding: 12, opacity: 0.6 }}>Nenhuma tarefa.</div>}
          </div>
        )}

        {tab === 2 && (
          <div style={{ marginTop: 12 }}>
            {sprints.map(s => (
              <Paper key={s.id} variant="outlined" style={{ padding: 12, marginBottom: 8, display: "flex", gap: 12, alignItems: "center" }}>
                <div style={{ flex: 1 }}>
                  <Typography variant="subtitle1">{s.name} <Chip size="small" label={s.status} style={{ marginLeft: 8 }} /></Typography>
                  {s.goal && <Typography variant="body2" color="textSecondary">{s.goal}</Typography>}
                  <Typography variant="caption">{s.startDate || "?"} → {s.endDate || "?"}</Typography>
                </div>
                {s.status === "planning" && <Button size="small" onClick={() => startSprint(s)}>Iniciar</Button>}
                {s.status === "active" && <Button size="small" onClick={() => closeSprint(s)}>Encerrar</Button>}
                <IconButton size="small" onClick={() => openEditSprint(s)}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => deleteSprint(s)}><DeleteIcon fontSize="small" /></IconButton>
              </Paper>
            ))}
            {sprints.length === 0 && <div style={{ opacity: 0.6 }}>Nenhuma sprint.</div>}
          </div>
        )}
      </div>

      {/* Task Dialog */}
      <Dialog open={taskDlg.open} onClose={() => setTaskDlg({ open: false, editing: null, form: {} })} maxWidth="sm" fullWidth>
        <DialogTitle>{taskDlg.editing ? `Tarefa #${taskDlg.editing.id}` : "Nova tarefa"}</DialogTitle>
        <DialogContent>
          <TextField label="Título" fullWidth margin="dense" value={taskDlg.form.title || ""}
            onChange={e => setTaskDlg({ ...taskDlg, form: { ...taskDlg.form, title: e.target.value } })} />
          <TextField label="Descrição" fullWidth margin="dense" multiline rows={3} value={taskDlg.form.description || ""}
            onChange={e => setTaskDlg({ ...taskDlg, form: { ...taskDlg.form, description: e.target.value } })} />
          <div style={{ display: "flex", gap: 8 }}>
            <TextField select label="Coluna" margin="dense" style={{ flex: 1 }} value={taskDlg.form.columnId || ""}
              onChange={e => setTaskDlg({ ...taskDlg, form: { ...taskDlg.form, columnId: e.target.value || null } })}>
              <MenuItem value="">—</MenuItem>
              {columns.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
            <TextField select label="Sprint" margin="dense" style={{ flex: 1 }} value={taskDlg.form.sprintId || ""}
              onChange={e => setTaskDlg({ ...taskDlg, form: { ...taskDlg.form, sprintId: e.target.value || null } })}>
              <MenuItem value="">—</MenuItem>
              {sprints.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
            </TextField>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <TextField select label="Status" margin="dense" style={{ flex: 1 }} value={taskDlg.form.status || "open"}
              onChange={e => setTaskDlg({ ...taskDlg, form: { ...taskDlg.form, status: e.target.value } })}>
              {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField select label="Prioridade" margin="dense" style={{ flex: 1 }} value={taskDlg.form.priority || "medium"}
              onChange={e => setTaskDlg({ ...taskDlg, form: { ...taskDlg.form, priority: e.target.value } })}>
              {PRIORITIES.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
            </TextField>
            <TextField select label="Tipo" margin="dense" style={{ flex: 1 }} value={taskDlg.form.type || "task"}
              onChange={e => setTaskDlg({ ...taskDlg, form: { ...taskDlg.form, type: e.target.value } })}>
              {TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </div>
          <TextField select label="Responsável" fullWidth margin="dense" value={taskDlg.form.assigneeId || ""}
            onChange={e => setTaskDlg({ ...taskDlg, form: { ...taskDlg.form, assigneeId: e.target.value || null } })}>
            <MenuItem value="">—</MenuItem>
            {users.map(u => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
          </TextField>
          <Autocomplete
            value={selectedClient}
            options={clientOptions}
            getOptionLabel={(o) => displayClientName(o)}
            getOptionSelected={(o, v) => o && v && o.id === v.id}
            filterOptions={(x) => x}
            loading={clientLoading}
            noOptionsText={(clientSearch || "").trim().length < 3 ? "Digite ao menos 3 letras (vazio = herda do projeto)" : "Nenhum cliente encontrado"}
            onInputChange={(_, val, reason) => {
              if (reason === "input") setClientSearch(val);
              if (reason === "clear") setClientSearch("");
            }}
            onChange={(_, val) => {
              setSelectedClient(val);
              setTaskDlg(d => ({ ...d, form: { ...d.form, clientId: val ? val.id : null } }));
            }}
            renderInput={(params) => (
              <TextField {...params} label="Cliente" margin="dense" fullWidth
                placeholder="Digite 3+ letras para buscar"
                helperText={selectedClient ? "" : "Em branco = herda cliente do projeto"}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (<>{clientLoading ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</>)
                }}
              />
            )}
          />
          <div style={{ display: "flex", gap: 8 }}>
            <TextField label="Início" type="date" InputLabelProps={{ shrink: true }} margin="dense" value={taskDlg.form.startDate || ""}
              onChange={e => setTaskDlg({ ...taskDlg, form: { ...taskDlg.form, startDate: e.target.value } })} />
            <TextField label="Prazo" type="date" InputLabelProps={{ shrink: true }} margin="dense" value={taskDlg.form.dueDate || ""}
              onChange={e => setTaskDlg({ ...taskDlg, form: { ...taskDlg.form, dueDate: e.target.value } })} />
            <TextField label="Estim. (h)" type="number" margin="dense" value={taskDlg.form.estimateHours || ""}
              onChange={e => setTaskDlg({ ...taskDlg, form: { ...taskDlg.form, estimateHours: e.target.value } })} />
          </div>
        </DialogContent>
        <DialogActions>
          {taskDlg.editing && <Button color="secondary" onClick={() => { deleteTask(taskDlg.editing); setTaskDlg({ open: false, editing: null, form: {} }); }}>Excluir</Button>}
          <Button onClick={() => setTaskDlg({ open: false, editing: null, form: {} })}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={saveTask}>Salvar</Button>
        </DialogActions>
      </Dialog>

      {/* Column Dialog */}
      <Dialog open={colDlg.open} onClose={() => setColDlg({ open: false, editing: null, boardId: null, form: {} })} maxWidth="xs" fullWidth>
        <DialogTitle>{colDlg.editing ? "Editar coluna" : "Nova coluna"}</DialogTitle>
        <DialogContent>
          <TextField label="Nome" fullWidth margin="dense" value={colDlg.form.name || ""}
            onChange={e => setColDlg({ ...colDlg, form: { ...colDlg.form, name: e.target.value } })} />
          <TextField label="Cor" type="color" margin="dense" value={colDlg.form.color || "#2196F3"}
            onChange={e => setColDlg({ ...colDlg, form: { ...colDlg.form, color: e.target.value } })} />
          <TextField label="Posição" type="number" margin="dense" value={colDlg.form.position ?? 0}
            onChange={e => setColDlg({ ...colDlg, form: { ...colDlg.form, position: Number(e.target.value) } })} />
          <TextField label="Limite WIP" type="number" margin="dense" value={colDlg.form.wipLimit || ""}
            onChange={e => setColDlg({ ...colDlg, form: { ...colDlg.form, wipLimit: e.target.value ? Number(e.target.value) : null } })} />
          <TextField select label="Coluna de conclusão" margin="dense" fullWidth value={colDlg.form.isDone ? "1" : "0"}
            onChange={e => setColDlg({ ...colDlg, form: { ...colDlg.form, isDone: e.target.value === "1" } })}>
            <MenuItem value="0">Não</MenuItem>
            <MenuItem value="1">Sim</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setColDlg({ open: false, editing: null, boardId: null, form: {} })}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={saveCol}>Salvar</Button>
        </DialogActions>
      </Dialog>

      {/* Sprint Dialog */}
      <Dialog open={sprintDlg.open} onClose={() => setSprintDlg({ open: false, editing: null, form: {} })} maxWidth="sm" fullWidth>
        <DialogTitle>{sprintDlg.editing ? "Editar sprint" : "Nova sprint"}</DialogTitle>
        <DialogContent>
          <TextField label="Nome" fullWidth margin="dense" value={sprintDlg.form.name || ""}
            onChange={e => setSprintDlg({ ...sprintDlg, form: { ...sprintDlg.form, name: e.target.value } })} />
          <TextField label="Objetivo" fullWidth multiline rows={2} margin="dense" value={sprintDlg.form.goal || ""}
            onChange={e => setSprintDlg({ ...sprintDlg, form: { ...sprintDlg.form, goal: e.target.value } })} />
          <div style={{ display: "flex", gap: 8 }}>
            <TextField label="Início" type="date" InputLabelProps={{ shrink: true }} margin="dense" value={sprintDlg.form.startDate || ""}
              onChange={e => setSprintDlg({ ...sprintDlg, form: { ...sprintDlg.form, startDate: e.target.value } })} />
            <TextField label="Fim" type="date" InputLabelProps={{ shrink: true }} margin="dense" value={sprintDlg.form.endDate || ""}
              onChange={e => setSprintDlg({ ...sprintDlg, form: { ...sprintDlg.form, endDate: e.target.value } })} />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSprintDlg({ open: false, editing: null, form: {} })}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={saveSprint}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default ProjectDetail;
